import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useSipDevice } from '@/hooks/useSipDevice';
import { useCallState } from '@/hooks/useCallState';
import { useUserNumbers } from '@/hooks/useUserNumbers';
import { useCallQueue } from '@/hooks/useCallQueue';
import { normalizeToE164 } from '@/lib/phone-validation';
import { router } from '@inertiajs/react';
import * as voipApi from '@/lib/voip/api';
import type { SipAccount } from '@/types/voip';

interface SoftphoneContextType {
  // Device state
  deviceState: 'offline' | 'registering' | 'registered' | 'error';
  deviceError: string | null;

  // Call state
  callState: 'idle' | 'dialing' | 'ringing' | 'connected' | 'ended' | 'error';
  callMetadata: any;
  activeCall: any;
  duration: number;
  formattedDuration: string;

  // Phone number
  phoneNumber: string;
  setPhoneNumber: (number: string) => void;

  // Recording
  enableRecording: boolean;
  setEnableRecording: (enabled: boolean) => void;

  // Incoming call
  incomingCall: any;

  // User numbers
  selectedNumber: any;
  userNumbers: any[];

  // Actions
  handleCall: () => Promise<void>;
  handleEndCall: () => void;
  handleAcceptCall: () => void;
  handleRejectCall: () => void;
  handleDigitClick: (digit: string) => void;
  toggleMute: () => void;
  toggleHold: () => void;
  callContact: (phoneNumber: string, contactId: number, contactName?: string, campaignContactId?: number) => void;

  // Widget UI state
  isWidgetExpanded: boolean;
  setIsWidgetExpanded: (expanded: boolean) => void;
  isWidgetVisible: boolean;
  setIsWidgetVisible: (visible: boolean) => void;

  // Error state
  callError: string | null;
  setCallError: (error: string | null) => void;

  // Configuration
  isConfigured: boolean;
  hasAssignedNumbers: boolean;

  // Contact-aware calling
  activeContactId: number | null;
  activeContactName: string | null;

  // Post-call disposition
  showDispositionModal: boolean;
  setShowDispositionModal: (show: boolean) => void;
  lastCallId: string | null;
  onDispositionSavedCallback: (() => void) | null;
  setOnDispositionSavedCallback: (cb: (() => void) | null) => void;

  // Call queue
  queueCount: number;
  queueEntries: { id: number; caller_number: string; position: number; wait_seconds: number; queued_at: string }[];
  dequeueNext: () => Promise<void>;
  isQueueLoading: boolean;
}

const SoftphoneContext = createContext<SoftphoneContextType | undefined>(undefined);

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  // ─── VoIP: Get default SIP account ─────────────────────────────────────
  const [sipAccount, setSipAccount] = useState<SipAccount | null>(null);
  const [sipAccountLoaded, setSipAccountLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await voipApi.listSipAccounts();
        if (cancelled) return;
        const def = resp.data.find(a => a.is_default && a.is_active)
          ?? resp.data.find(a => a.is_active)
          ?? null;
        setSipAccount(def);
      } catch (err) {
        console.error('Failed to load SIP accounts:', err);
      } finally {
        if (!cancelled) setSipAccountLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Determine if we should use SIP path
  const useSipPath = sipAccount !== null && sipAccount.is_active;

  // Initialize SIP device (replaces Twilio)
  const {
    deviceState: sipDeviceState,
    error: sipDeviceError,
    currentSession,
    currentCallState: sipCallState,
    makeCall: sipMakeCall,
    hangup: sipHangup,
    acceptCall: sipAcceptCall,
    rejectCall: sipRejectCall,
    sendDTMF: sipSendDTMF,
    toggleMute: sipToggleMute,
  } = useSipDevice(useSipPath ? sipAccount!.id : null);

  // Map SIP device state to Twilio-compatible states
  const deviceState = useSipPath ? sipDeviceState : 'offline';
  const deviceError = useSipPath ? sipDeviceError : null;

  // Map SIP call state to legacy call state
  const mapSipCallStateToLegacy = (state: string): 'idle' | 'dialing' | 'ringing' | 'connected' | 'ended' | 'error' => {
    switch (state) {
      case 'idle': return 'idle';
      case 'ringing': return 'dialing';
      case 'incoming': return 'ringing';
      case 'answered': return 'connected';
      case 'hangup': return 'ended';
      case 'busy':
      case 'failed': return 'error';
      default: return 'idle';
    }
  };

  const callStateLegacy = useSipPath ? mapSipCallStateToLegacy(sipCallState) : 'idle';

  // For compatibility: incoming call detection
  const incomingCall = useSipPath && sipCallState === 'incoming' ? { parameters: { From: 'Unknown' } } : null;

  const {
    callMetadata,
    activeCall,
    duration,
    formattedDuration,
    initiateCall,
    endCall,
    toggleMute: legacyToggleMute,
    toggleHold,
    sendDTMF: legacySendDTMF,
    setDuration,
    startDurationTimer,
    stopDurationTimer,
  } = useCallState();

  // Drive the duration timer from the SIP path (JsSIP). useCallState's own
  // timer only starts via the Twilio call.on('accept') callback, which never
  // fires on the SIP/JsSIP path — so the on-screen call timer stayed at 0.
  useEffect(() => {
    if (!useSipPath) return;
    if (sipCallState === 'answered') {
      setDuration(0);
      startDurationTimer();
    } else if (sipCallState === 'hangup' || sipCallState === 'idle') {
      stopDurationTimer();
    }
  }, [sipCallState, useSipPath, setDuration, startDurationTimer, stopDurationTimer]);

  const { selectedNumber, numbers: userNumbers } = useUserNumbers();

  // Call queue — polls while user is in a call
  const isInCall = ['dialing', 'ringing', 'connected'].includes(callStateLegacy);
  const { queueCount, queueEntries, dequeueNext, isLoading: isQueueLoading } = useCallQueue(isInCall);

  // Local state
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [enableRecording, setEnableRecording] = useState(true);
  const [callError, setCallError] = useState<string | null>(null);
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);
  const [isWidgetVisible, setIsWidgetVisible] = useState(true);

  // Contact-aware calling state
  const [activeContactId, setActiveContactId] = useState<number | null>(null);
  const [activeContactName, setActiveContactName] = useState<string | null>(null);
  const [activeCampaignContactId, setActiveCampaignContactId] = useState<number | null>(null);

  // Post-call disposition state
  const [showDispositionModal, setShowDispositionModal] = useState(false);
  const [lastCallId, setLastCallId] = useState<string | null>(null);
  const [onDispositionSavedCallback, setOnDispositionSavedCallback] = useState<(() => void) | null>(null);
  const dispositionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Current call session ID for SIP calls
  const currentCallSessionId = useRef<number | null>(null);

  // Configuration from shared Inertia props (provided by HandleInertiaRequests)
  const [isConfigured, setIsConfigured] = useState(true);
  const [hasAssignedNumbers, setHasAssignedNumbers] = useState(true);

  // Read softphone config from shared Inertia props on mount and navigation
  useEffect(() => {
    const syncConfig = () => {
      const pageProps = (router.page?.props || {}) as any;
      if (pageProps.softphone) {
        setIsConfigured(pageProps.softphone.isConfigured !== false);
        setHasAssignedNumbers(pageProps.softphone.hasAssignedNumbers !== false);
      }
    };

    syncConfig();

    const unsubscribe = router.on('navigate', syncConfig);
    return () => unsubscribe();
  }, []);

  // Auto-expand widget on incoming call or when call becomes active
  useEffect(() => {
    if (incomingCall || ['dialing', 'ringing', 'connected'].includes(callStateLegacy)) {
      setIsWidgetExpanded(true);
    }
  }, [incomingCall, callStateLegacy]);

  // Show disposition modal when call ends (only for contact-linked calls)
  useEffect(() => {
    if (callStateLegacy === 'ended' && lastCallId && activeContactId) {
      if (dispositionTimerRef.current) {
        clearTimeout(dispositionTimerRef.current);
      }
      dispositionTimerRef.current = setTimeout(() => {
        setShowDispositionModal(true);
        dispositionTimerRef.current = null;
      }, 1200);
    }
    if (callStateLegacy === 'idle' && !showDispositionModal && !dispositionTimerRef.current) {
      setActiveContactId(null);
      setActiveContactName(null);
      setLastCallId(null);
      currentCallSessionId.current = null;
    }
  }, [callStateLegacy, lastCallId, activeContactId, showDispositionModal]);

  // Auto-dequeue: when call ends and there are callers waiting
  const prevCallState = useRef(callStateLegacy);
  useEffect(() => {
    if (prevCallState.current === 'connected' && callStateLegacy === 'ended' && queueCount > 0) {
      const timer = setTimeout(() => {
        console.log('Auto-dequeue: connecting next queued caller');
        dequeueNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
    prevCallState.current = callStateLegacy;
  }, [callStateLegacy, queueCount, dequeueNext]);

  // Handle outbound call
  const handleCall = async () => {
    if (callStateLegacy !== 'idle') {
      console.warn('Cannot make call: already in call state:', callStateLegacy);
      return;
    }

    if (!phoneNumber || phoneNumber.toString().replace(/\D/g, '').length < 10) {
      setCallError('Please enter a valid phone number (at least 10 digits)');
      return;
    }

    try {
      setCallError(null);

      const fromNumber = selectedNumber?.number;
      if (!fromNumber) {
        setCallError('No phone number selected. Please assign a phone number first.');
        return;
      }

      console.log('Initiating SIP call...', { phoneNumber, fromNumber, enableRecording, contactId: activeContactId });

      // First create the call record in the database
      const response = await window.axios.post('/calls/initiate', {
        to_number: phoneNumber,
        from_number: fromNumber,
        number_source: selectedNumber?.source || 'sip',
        enable_recording: enableRecording,
        contact_id: activeContactId || undefined,
        campaign_contact_id: activeCampaignContactId || undefined,
      });

      const callData = response.data.call;

      if (!callData || !callData.id) {
        throw new Error('Failed to create call record');
      }

      setLastCallId(callData.id.toString());

      // Make SIP call
      const session = await sipMakeCall(phoneNumber, callData.id);

      if (session) {
        console.log('SIP call successful');
        currentCallSessionId.current = (session as any).callSessionId || null;
        // Update call state to dialing/connected via useCallState
        // Since we're using SIP, we need to manually track the call state
        // The session events will update sipCallState
      } else {
        throw new Error('Failed to establish SIP connection');
      }
    } catch (error: any) {
      console.error('Failed to initiate call:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initiate call';
      setCallError(errorMessage);
      setTimeout(() => setCallError(null), 5000);
    }
  };

  // Handle end call
  const handleEndCall = () => {
    console.log('Ending SIP call...');
    if (useSipPath) {
      sipHangup();
    } else {
      endCall();
    }
    setCallError(null);
    setPhoneNumber('');
  };

  // Click-to-call a specific contact
  const callContact = useCallback((phone: string, contactId: number, contactName?: string, campaignContactId?: number) => {
    setActiveContactId(contactId);
    setActiveContactName(contactName || null);
    setActiveCampaignContactId(campaignContactId || null);
    setPhoneNumber(normalizeToE164(phone, 'US'));
    setIsWidgetExpanded(true);
  }, []);

  // Handle incoming call acceptance
  const handleAcceptCall = () => {
    if (useSipPath && sipCallState === 'incoming') {
      sipAcceptCall();
    }
  };

  // Handle incoming call rejection
  const handleRejectCall = () => {
    if (useSipPath && sipCallState === 'incoming') {
      sipRejectCall();
    }
  };

  // Handle digit click (DTMF)
  const handleDigitClick = (digit: string) => {
    if (callStateLegacy === 'connected') {
      if (useSipPath) {
        sipSendDTMF(digit);
      } else {
        legacySendDTMF(digit);
      }
    } else {
      setPhoneNumber((prev) => (prev || '') + digit);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (useSipPath) {
      sipToggleMute();
    } else {
      legacyToggleMute();
    }
  };

  const value: SoftphoneContextType = {
    // Device state (mapped from SIP)
    deviceState,
    deviceError,

    // Call state (mapped from SIP)
    callState: callStateLegacy,
    callMetadata,
    activeCall,
    duration,
    formattedDuration,

    // Phone number
    phoneNumber,
    setPhoneNumber,

    // Recording
    enableRecording,
    setEnableRecording,

    // Incoming call
    incomingCall,

    // User numbers
    selectedNumber,
    userNumbers,

    // Actions
    handleCall,
    handleEndCall,
    handleAcceptCall,
    handleRejectCall,
    handleDigitClick,
    toggleMute,
    toggleHold,
    callContact,

    // Widget UI state
    isWidgetExpanded,
    setIsWidgetExpanded,
    isWidgetVisible,
    setIsWidgetVisible,

    // Error state
    callError,
    setCallError,

    // Configuration
    isConfigured,
    hasAssignedNumbers,

    // Contact-aware calling
    activeContactId,
    activeContactName,

    // Post-call disposition
    showDispositionModal,
    setShowDispositionModal: (show: boolean) => {
      setShowDispositionModal(show);
      if (!show) {
        setActiveContactId(null);
        setActiveContactName(null);
        setActiveCampaignContactId(null);
        setLastCallId(null);
        currentCallSessionId.current = null;
      }
    },
    lastCallId,
    onDispositionSavedCallback,
    setOnDispositionSavedCallback,

    // Call queue
    queueCount,
    queueEntries,
    dequeueNext,
    isQueueLoading,
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
}

export function useSoftphone() {
  const context = useContext(SoftphoneContext);
  if (context === undefined) {
    throw new Error('useSoftphone must be used within a SoftphoneProvider');
  }
  return context;
}